import React, {
	PropsWithChildren,
	memo,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
	ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import styled, { css } from "styled-components";
import Draggable from "react-draggable";
import { uniq, isBoolean, isNil, isEmpty, trim } from "lodash";
import { CheckBox, Icon, theme } from "uikit";

import { useKeyBind } from "../../../../hooks";
import { sortByNumberOrString } from "../../../../utils";
import Modal from "../../../Modal";
import KeyBind from "../../../KeyBind";
import { HeaderMemo, FooterMemo, Footer } from "../../../common/Modal";
import {
	StyledColumn,
	StyledRow,
	StyledSpan,
	StyledP,
	Divider,
	CheckBoxWithText,
	SuspenseLoader,
} from "../../../common";

import { SearchBox, ListCheckBoxes } from "./components";

const Chips = styled(StyledP)`
	justify-content: center;
	align-items: center;
	gap: 10px;
	height: 24px;
	padding: 4px 8px;
	border-radius: 4px;
	background: #f5f5f5;
	font-family: Lato;
	font-weight: 500;
	font-size: 13px;
	line-height: 16px;
`;

const ClearChips = styled(Chips)`
	color: #f83528;
	background: transparent;
	cursor: pointer;
`;

const SortArrow = styled(StyledRow)<{ sortType?: "desc" | "asc" }>`
	display: block;
	position: relative;
	box-sizing: border-box;
	width: 6px;
	height: 10px;
	transform: rotate(-45deg);
	-webkit-transform: rotate(-45deg);

	&:after,
	&:before {
		content: "";
		display: block;
		box-sizing: border-box;
		position: absolute;
		width: 5px;
		height: 5px;
		transform: translate(50%, 50%);
		-webkit-transform: translate(50%, 50%);
	}

	&:after {
		border-bottom: 2px solid #03a9f4;
		border-left: 2px solid #03a9f4;
		bottom: -1px;
		left: -2px;
	}
	&:before {
		border-top: 2px solid #03a9f4;
		border-right: 2px solid #03a9f4;
		top: 2px;
		right: -1px;
	}
	${({ sortType }) => {
		if (sortType === "desc") {
			return css`
				&:before {
					content: "";
					display: block;
					position: absolute;
					box-sizing: border-box;
					width: 1px;
					height: 7px;
					background-color: #03a9f4;
					transform: rotate(-135deg) translate(50%, 0px);
					-webkit-transform: rotate(-135deg) translate(50%, 0px);

					left: 4px;
					top: 7px;
				}
			`;
		}
		if (sortType === "asc") {
			return css`
				&:after {
					content: "";
					display: block;
					position: absolute;
					box-sizing: border-box;
					width: 1px;
					height: 7px;
					background-color: #03a9f4;
					transform: rotate(-135deg) translate(50%, 1px);
					-webkit-transform: rotate(-135deg) translate(50%, 1px);

					left: 4px;
					top: 7px;
				}
			`;
		}

		return "";
	}};
`;

const ListSelect: React.FC<ListSelect.Props> = ({
	value,
	options,
	title,
	required = false,
	children,
	footer,
	header,
	headerDivider,
	footerDivider,
	onSubmit,
	onClose,
	pullUpItemInsideArray = false,
	hiddenButton = true,
	warnText,
	clearText,
	showSelectAll,
	showAllItem,
	showActiveItemBase = true,
	modalSetting,
	transactionText,
	selectOnlyOne = false,
	sort,
	valueSearch,
	saveSearch,
	loadingList = false,
	onScrollMaxVisible,
	showTaxiServices = false,
	showCompanies = false,
}) => {
	const { t } = useTranslation();
	const refSearch = useRef<HTMLInputElement | null>(null);
	const [query, setQuery] = useState<string>("");
	const [selected, setSelected] = useState<ListSelect.Options>(value || []);

	const [filteredOptions, setOptions] = useState<ListSelect.Options>([]);
	const [sortType, setSortType] = useState<ListSelect.SortType | undefined>(
		sort?.sortType,
	);
	const [activeOption, setActiveOption] = useState(-1);
	const [warning, setWarning] = useState<boolean>(false);
	const [showActiveItem, setShowActiveItem] = useState<boolean>(true);
	const [isFocusSearch, setIsFocusSearch] = useState<boolean>(false);

	const saveQueryGlobal = useCallback(
		(value: string) => {
			if (saveSearch) saveSearch(value);
			setQuery(value);
		},
		[saveSearch],
	);

	useLayoutEffect(() => {
		setShowActiveItem(showActiveItemBase);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useLayoutEffect(() => {
		if (sort) setSortType(sort?.sortType);
	}, [sort]);

	const handelSortType = useCallback(
		(value: ListSelect.SortType | undefined) => {
			setSortType(value);
			saveQueryGlobal("");
			if (sort && sort.onSort && value) sort.onSort(value);
		},
		[saveQueryGlobal, sort],
	);

	const data = useMemo(() => {
		if (sort?.active && sortType) {
			const { active } = sort;
			if (active && sortType) {
				const payload = [...options]?.sort((prev, next) => {
					const prevValue =
						sortType === "asc" ? prev?.label : next?.label;
					const nextValue =
						sortType === "asc" ? next?.label : prev?.label;
					return sortByNumberOrString(
						prevValue || "",
						nextValue || "",
					);
				});
				return payload;
			}
		}

		return options;
	}, [options, sort, sortType]);

	const actualOptions = useMemo(
		() => (query.length ? filteredOptions : data),
		[filteredOptions, data, query.length],
	);

	const handleSelectAdd = useCallback(
		(item) => {
			if (!item) return;

			if (selectOnlyOne) {
				selected.find((option) => option.value === item.value) ||
					setSelected([item]);
				return;
			}

			selected.find((option) => option.value === item.value) ||
				setSelected([...selected, item]);
		},
		[selected, selectOnlyOne],
	);

	const handleSelectRemove = useCallback(
		(item) => {
			if (!item) return;
			!selected.find((option) => option.value === item.value) ||
				setSelected(selected.filter((v) => v.value !== item.value));
		},
		[selected],
	);

	const handleSelect = useCallback(
		(item) => {
			if (!item) return;

			const isActive = !!selected.find(
				(option) => option.value === item.value,
			);

			if (isActive) {
				setSelected(selected.filter((v) => v.value !== item.value));
			} else if (selectOnlyOne) {
				setSelected([item]);
			} else {
				setSelected([...selected, item]);
			}
		},
		[selected, selectOnlyOne],
	);

	const handleSubmit = useCallback(() => {
		if (selected.length) {
			onSubmit(selected);
		} else if (required) {
			setWarning(true);
		} else {
			onSubmit([]);
		}
	}, [onSubmit, selected, required]);

	const getOptions = useCallback(
		(item: string) => {
			const existItem = data?.filter((option) =>
				option?.name
					?.toLocaleLowerCase()
					?.includes(item?.toLocaleLowerCase()),
			);

			if (pullUpItemInsideArray) {
				const noExistItem = data.filter(
					(option) =>
						!option.name
							?.toLocaleLowerCase()
							?.includes(item?.toLocaleLowerCase()),
				);
				return [...existItem, ...noExistItem];
			}

			return existItem;
		},
		[data, pullUpItemInsideArray],
	);

	const onChangeSearchTextBox = useCallback(
		(value: string) => {
			// const isCheck =
			// 	!value.trim().includes("+") && !value.trim().includes("-");

			//* If the mouse is hovering over the input — allow any input (including trailing spaces) If not — prevent input if it ends with a space
			const isCheck = isFocusSearch || !value.endsWith(" ");

			if (isCheck) {
				handelSortType(undefined);
				saveQueryGlobal(value);
				setOptions(getOptions(value));
				setActiveOption(0);
			}
		},
		[getOptions, handelSortType, saveQueryGlobal, isFocusSearch],
	);

	const validateString = useCallback(
		(value: string | undefined): boolean => {
			if (isNil(value)) return false;
			const isCheck = isFocusSearch ? false : isEmpty(trim(value));
			if (typeof value === "string" && isCheck) return false;
			return true;
		},
		[isFocusSearch],
	);

	// --- Events ---
	useEffect(() => {
		const timer = setTimeout(() => {
			refSearch?.current?.focus();
			if (validateString(valueSearch))
				onChangeSearchTextBox(valueSearch ?? "");
		}, 30);

		return () => clearTimeout(timer);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const nextElementById = useCallback(
		(elemId: string, load = false): HTMLElement | null => {
			const elem = document.getElementById(elemId);
			setTimeout(() => {
				if (!load) {
					if (elem) elem?.focus();
				}
			}, 10);
			return elem;
		},
		[],
	);

	useEffect(() => {
		const handleKeyboardEvent = (event: KeyboardEvent) => {
			const keyEvent = event?.code;
			const isArrows = [
				"arrowup",
				"arrowdown",
				"arrowright",
				"arrowleft",
				"space",
				"tab",
			].includes(keyEvent.toLowerCase());

			if (keyEvent === "Space" && !isFocusSearch) {
				event.stopPropagation();
				event.preventDefault();

				const item = actualOptions[activeOption];
				if (
					showActiveItem &&
					isBoolean(item.active) &&
					!item.active &&
					!selected.find((option) => option.value === item.value)
				) {
					return;
				}

				handleSelect(item);
			}
			if (keyEvent === "Tab") {
				const isFocusInSearch =
					refSearch?.current === document.activeElement ||
					isFocusSearch;

				const isNotEmptyList = !!options.length;
				const isNotActive = activeOption === -1;

				if (isNotEmptyList && !(!isFocusInSearch && isNotActive)) {
					event.preventDefault();
					event.stopPropagation();

					if (isFocusInSearch) {
						refSearch?.current?.blur();
					}

					const choiseSearchOnSelect =
						isFocusInSearch && !isNotActive ? 0 : 1;

					if (activeOption + 1 > actualOptions.length - 1) {
						nextElementById("modal_footer_button_save_id");
						setActiveOption(-1);
					} else {
						setActiveOption((prev) =>
							prev + 1 > actualOptions.length - 1
								? 0
								: prev + choiseSearchOnSelect,
						);
					}
				}
			}
			if (keyEvent === "ArrowUp") {
				event.stopPropagation();
				event.preventDefault();
				setActiveOption((prev) =>
					prev - 1 < 0 ? actualOptions.length - 1 : prev - 1,
				);
			}
			if (keyEvent === "ArrowDown") {
				event.stopPropagation();
				event.preventDefault();
				setActiveOption((prev) =>
					prev + 1 > actualOptions.length - 1 ? 0 : prev + 1,
				);
			}
			if (keyEvent === "ArrowLeft") {
				event.stopPropagation();
				event.preventDefault();
				const item = actualOptions[activeOption];
				if (
					showActiveItem &&
					isBoolean(item.active) &&
					!item.active &&
					!selected.find((option) => option.value === item.value)
				) {
					return;
				}
				handleSelectAdd(item);
			}
			if (keyEvent === "ArrowRight") {
				event.stopPropagation();
				event.preventDefault();
				const item = actualOptions[activeOption];
				if (
					showActiveItem &&
					isBoolean(item.active) &&
					!item.active &&
					!selected.find((option) => option.value === item.value)
				) {
					return;
				}
				handleSelectRemove(item);
			}

			if (isArrows) event.stopPropagation();
		};
		document.addEventListener("keydown", handleKeyboardEvent, true);
		return function cleanup() {
			document.removeEventListener("keydown", handleKeyboardEvent, true);
		};
	}, [
		actualOptions.length,
		activeOption,
		handleSelect,
		actualOptions,
		handleSelectAdd,
		handleSelectRemove,
		selected,
		showActiveItem,
		isFocusSearch,
		options.length,
		nextElementById,
	]);

	useKeyBind(["Escape"], (e) => {
		onClose();
		e.preventDefault();
		e.stopPropagation();
	});

	useKeyBind(["ShiftLeft", "Equal"], () => {
		if (activeOption !== -1) {
			if (selectOnlyOne) {
				setSelected(uniq([actualOptions[activeOption]]));
			} else {
				setSelected(uniq([...selected, actualOptions[activeOption]]));
			}

			saveQueryGlobal("");
		}
	});
	useKeyBind(["NumpadAdd"], () => {
		if (activeOption !== -1) {
			if (selectOnlyOne) {
				setSelected(uniq([actualOptions[activeOption]]));
			} else {
				setSelected(uniq([...selected, actualOptions[activeOption]]));
			}
			saveQueryGlobal("");
		}
	});

	useKeyBind(["Minus"], () => {
		if (activeOption !== -1) {
			setSelected(
				selected.filter(
					(v) => v.value !== actualOptions[activeOption].value,
				),
			);
			saveQueryGlobal("");
		}
	});

	const selectedAll = useCallback(
		(isAll: boolean) => {
			if (!isAll) setSelected([]);
			else if (!showActiveItem) {
				setSelected(data);
			} else {
				const items = data.filter((item) =>
					isBoolean(item?.active) ? item?.active === true : true,
				);
				setSelected(items);
			}
		},
		[data, showActiveItem],
	);

	const all = useMemo(() => {
		if (!showActiveItem) {
			return selected.length === data?.length;
		}
		const items = data.filter((item) =>
			isBoolean(item?.active) ? item?.active === true : true,
		);
		return selected.length >= items?.length;
	}, [showActiveItem, data, selected.length]);

	const translateText = useMemo(() => {
		const _warnText =
			warnText ?? t("orders.modals.listSelect.str200") ?? "";
		const _clearText =
			clearText ?? t("orders.modals.listSelect.str201") ?? "";
		const hide = t("actives.hide");
		const inactive = t("filter.options.inactive");
		const notActive = t("actives.false");
		return { hide, inactive, notActive, _warnText, _clearText };
	}, [t, warnText, clearText]);

	const inactiveText = useMemo(() => {
		const { hide, inactive } = translateText;
		return `${hide} ${inactive}`;
	}, [translateText]);

	return (
		<Modal {...modalSetting}>
			<KeyBind
				keys={["Escape"]}
				onUse={() => {
					onClose();
				}}
			/>
			<KeyBind
				keys={["Enter"]}
				onUse={() => {
					handleSubmit();
				}}
			/>
			<Draggable handle=".handle">
				<StyledColumn br="10px" shadow="0px 0px 10px 2px gray">
					<HeaderMemo
						title={title}
						onClose={onClose}
						divider={headerDivider}
					>
						{header}
					</HeaderMemo>

					<StyledColumn
						gap="10px"
						alignItems="start"
						w="732px"
						h="537px"
						bgC="#ffffff"
					>
						<SearchBox
							inputRef={refSearch}
							value={query}
							onChange={onChangeSearchTextBox}
							transactionText={transactionText}
							onFocusChangeSearch={setIsFocusSearch}
						/>

						{loadingList ? (
							<StyledRow
								w="100%"
								h="100%"
								alignItems="center"
								justify="center"
							>
								<SuspenseLoader loader="1" />
							</StyledRow>
						) : (
							<>
								<StyledRow
									gap="3px"
									w="100%"
									flex={{ wrap: "wrap" }}
									h={{ min: "26px" }}
									justify="start"
									p="0 24px 20px 24px"
									overY={!selectOnlyOne ? "scroll" : "hidden"}
									scrollbar={!selectOnlyOne}
								>
									{selected.map((item) => (
										<Chips key={item.value}>
											{item.name}
											<StyledSpan
												cursor="pointer"
												onClick={() =>
													setSelected(
														selected.filter(
															(v) =>
																v.value !==
																item.value,
														),
													)
												}
											>
												<Icon
													id="close"
													size={8}
													colors={[
														theme.colors
															.disabled_text,
													]}
												/>
											</StyledSpan>
										</Chips>
									))}
									{Boolean(selected.length) && (
										<ClearChips
											onClick={() => setSelected([])}
										>
											<Icon
												id="close"
												size={8}
												colors={[theme.colors.negative]}
											/>
											{translateText._clearText}
										</ClearChips>
									)}
								</StyledRow>
								<Divider
									side="top"
									gap="0 10px"
									justify="start"
									w="100%"
								>
									{showSelectAll && (
										<StyledRow
											alignItems="start"
											p="5px 10px 0 10px"
										>
											<CheckBox
												value={all}
												onChange={selectedAll}
											/>
										</StyledRow>
									)}
									{sort?.show && (
										<StyledRow
											alignItems="center"
											justify="start"
											w="max-content"
											p="5px 10px 0 10px"
											gap="0 3px"
											onClick={() => {
												handelSortType(
													sortType === "asc"
														? "desc"
														: "asc",
												);
											}}
											cursor="pointer"
										>
											<StyledP>
												{t([
													sort?.label || "",
													"title",
													"Name",
												])}
											</StyledP>

											<SortArrow sortType={sortType} />
										</StyledRow>
									)}
									{showAllItem && (
										<StyledRow
											alignItems="start"
											p="5px 10px 0 10px"
										>
											<CheckBoxWithText
												value={showActiveItem}
												onChange={setShowActiveItem}
												title={inactiveText}
											/>
										</StyledRow>
									)}
								</Divider>
								<ListCheckBoxes
									options={actualOptions}
									activeOption={activeOption}
									selected={selected}
									selectOnlyOne={selectOnlyOne}
									setSelected={setSelected}
									setWarning={setWarning}
									handleSelect={handleSelect}
									translateText={translateText}
									onScrollMaxVisible={onScrollMaxVisible}
									showActiveItem={showActiveItem}
									showTaxiServices={showTaxiServices}
									showCompanies={showCompanies}
								/>
								{children}
							</>
						)}
					</StyledColumn>

					<FooterMemo
						onSave={() => handleSubmit()}
						onClose={onClose}
						divider={footerDivider}
						hiddenButton={hiddenButton}
						buttonSave={{
							disabled: loadingList,
						}}
					>
						{warning && (
							<StyledSpan colors="#F83528">
								{translateText._warnText}
							</StyledSpan>
						)}
						{footer}
					</FooterMemo>
				</StyledColumn>
			</Draggable>
		</Modal>
	);
};

declare namespace ListSelect {
	interface Option {
		/** Name for selection */
		name: string;
		/** Key for selection */
		value: number;
		active?: boolean;
		data?: Record<string, any>;
		key?: string | number;
		label?: string;
		iconBeforeText?: ReactNode;
		disabled?: boolean;
		labelRigth?: string;
	}

	type Options = Option[];
	type SortType = "desc" | "asc";

	interface Props extends PropsWithChildren {
		value: Options | undefined;
		options: Options;
		title?: string;
		required?: boolean;
		onSubmit: (value: Options) => void;
		onClose: Footer.Props["onClose"];
		footer?: ReactNode;
		footerDivider?: boolean;
		headerDivider?: boolean;
		header?: ReactNode;
		pullUpItemInsideArray?: boolean;
		hiddenButton?: boolean;
		warnText?: string | ReactNode;
		clearText?: string | ReactNode;
		valueSearch?: string;
		saveSearch?: (value: string) => void;
		loadingList?: boolean;
		onScrollMaxVisible?: (index: number) => void;

		showTaxiServices?: boolean;
		showCompanies?: boolean;
		showSelectAll?: boolean;
		showAllItem?: boolean;
		showActiveItemBase?: boolean;
		selectOnlyOne?: boolean;

		modalSetting?: Modal.Props;
		transactionText?: {
			query?: string;
			sortLabel?: string;
			warnText?: string | ReactNode;
			clearText?: string | ReactNode;
			title?: string;
		};
		sort?: {
			show?: boolean;
			active?: boolean;
			sortType?: SortType;
			label?: string;
			onSort?: (type: SortType) => void;
		};
	}
}

export const ListSelectMemo = memo(ListSelect);
export default ListSelect;
